import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, share } from 'rxjs/operators';
import { PagedResponse, SortDirection } from '../core';
import { handleCsvError } from '../http-shared/error-handlers';
import { currencyFromApi } from '../pricing';
import {
  CreateStripeExternalAccountRequest,
  GetMerchantRequest,
  GetMultiRetailConfigurationsRequest,
  GetMultiRetailConfigurationsResponse,
  GetOutstandingBalanceRequest,
  GetPayoutSummaryRequest,
  GetRetailPayoutRequest,
  GetStatisticsRequest,
  GetStatisticsResponse,
  HostService,
  ListRetailPayoutsRequest,
  ListRetailPayoutsResponse,
  MerchantApiService,
  PagedRequestOptions,
  PagedRequestOptionsInterface,
  SearchMerchantsRequestSortBy,
  SortDirection as SortDirectionApi,
  UpsertDefaultRetailCustomerConfigurationRequest,
  UpsertRetailConfigurationRequest,
  MerchantServicesReportRun,
  MerchantServicesReportRunType,
  AccountBalance,
} from '@vendasta/billing';
import { Balance } from './balance';
import { Merchant, MerchantFromAPI } from './merchant';
import { DownloadMerchantsCsvRequest } from './merchant.interface';
import { PayoutSummary, payoutSummaryFromAPI } from './payout-summary';
import {
  DefaultCustomerConfiguration,
  defaultCustomerConfigurationToApi,
  RetailConfiguration,
  retailConfigurationFromApi,
} from './retail-configuration';
import { RetailPayout, retailPayoutFromApi } from '../payment';

/**
 * @enum SortMerchantsBy
 * Names should match corresponding field in Merchant interface.
 * Values should match ../_internal/enums/SortBy
 */
export enum SortMerchantsBy {
  merchantId = 0,
  companyName,
  contactName,
  emailAddress,
  country,
  state,
  created,
  updated,
}

@Injectable({ providedIn: 'root' })
export class MerchantService {
  constructor(private merchantApi: MerchantApiService, private hostService: HostService, private http: HttpClient) {}

  get(merchantId: string): Observable<Merchant> {
    const req = new GetMerchantRequest({ merchantId });
    return this.merchantApi.get(req).pipe(map((resp) => MerchantFromAPI(resp.merchant)));
  }

  create(
    merchantId: string,
    address: string,
    city: string,
    state: string,
    country: string,
    zipCode: string,
    emailAddress: string,
    phoneNumber: string,
    contactName: string,
    companyName: string,
    additionalEmailAddresses?: string[],
  ): Observable<boolean> {
    return this.merchantApi
      .create({
        merchantId,
        address,
        city,
        state,
        country,
        zipCode,
        emailAddress,
        phoneNumber,
        contactName,
        companyName,
        additionalEmailAddresses,
      })
      .pipe(map(() => true));
  }

  update(
    merchantId: string,
    address: string,
    city: string,
    state: string,
    country: string,
    zipCode: string,
    emailAddress: string,
    phoneNumber: string,
    contactName: string,
    companyName: string,
    additionalEmailAddresses?: string[],
  ): Observable<boolean> {
    return this.merchantApi
      .update({
        merchantId,
        address,
        city,
        state,
        country,
        zipCode,
        emailAddress,
        phoneNumber,
        contactName,
        companyName,
        additionalEmailAddresses,
      })
      .pipe(map(() => true));
  }

  getOutstandingBalance(merchantId: string): Observable<Balance> {
    const req = new GetOutstandingBalanceRequest({ merchantId });
    return this.merchantApi.getOutstandingBalance(req).pipe(
      map((r) => ({
        outstanding: r.outstandingBalance || 0,
        currency: currencyFromApi(r.currency),
      })),
    );
  }

  getStatistics(merchantId: string): Observable<GetStatisticsResponse> {
    const req = new GetStatisticsRequest({ merchantId });
    return this.merchantApi.getStatistics(req);
  }

  private sortMerchantByToApi(sortBy: SortMerchantsBy): SearchMerchantsRequestSortBy {
    switch (sortBy) {
      case SortMerchantsBy.merchantId:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_MERCHANT_ID;
      case SortMerchantsBy.companyName:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_COMPANY_NAME;
      case SortMerchantsBy.contactName:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_CONTACT_NAME;
      case SortMerchantsBy.emailAddress:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_EMAIL_ADDRESS;
      case SortMerchantsBy.country:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_COUNTRY;
      case SortMerchantsBy.state:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_STATE;
      case SortMerchantsBy.updated:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_UPDATED;
      case SortMerchantsBy.created:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_CREATED;
      default:
        return SearchMerchantsRequestSortBy.SEARCH_MERCHANTS_REQUEST_SORT_BY_MERCHANT_ID;
    }
  }

  private sortDirectionToApi(sortDirection: SortDirection): SortDirectionApi {
    switch (sortDirection) {
      case SortDirection.Ascending:
        return SortDirectionApi.SORT_DIRECTION_ASCENDING;
      case SortDirection.Descending:
        return SortDirectionApi.SORT_DIRECTION_DESCENDING;
      default:
        return SortDirectionApi.SORT_DIRECTION_ASCENDING;
    }
  }

  search(
    searchTerm: string,
    sortBy: SortMerchantsBy,
    sortDirection: SortDirection,
    pagingOptions: PagedRequestOptionsInterface,
  ): Observable<PagedResponse<Merchant>> {
    return this.merchantApi
      .search({
        searchTerm: searchTerm,
        sortBy: this.sortMerchantByToApi(sortBy),
        sortDirection: this.sortDirectionToApi(sortDirection),
        pagingOptions: new PagedRequestOptions(pagingOptions),
      })
      .pipe(
        map(
          (r) =>
            new PagedResponse(
              r.merchants.map((m) => MerchantFromAPI(m)),
              r.pagingMetadata.nextCursor,
              r.pagingMetadata.hasMore,
            ),
        ),
      );
  }

  setAutoPostInvoices(merchantId: string, autoPostInvoices: boolean): Observable<boolean> {
    return this.merchantApi
      .setAutoPostInvoices({
        merchantId: merchantId,
        shouldAutoPostInvoices: autoPostInvoices,
      })
      .pipe(map(() => true));
  }

  setAutoChargeInvoices(merchantId: string, autoChargeInvoices: boolean): Observable<boolean> {
    return this.merchantApi
      .setAutoChargeInvoices({
        merchantId,
        autoChargeInvoices,
      })
      .pipe(map(() => true));
  }

  downloadCsv(r: DownloadMerchantsCsvRequest): Observable<Blob> {
    const queryParams = `?filename=${r.filename}`;
    return this.http
      .get(this.hostService.hostWithScheme + '/merchants-csv' + queryParams, this.apiOptions())
      .pipe(catchError(handleCsvError));
  }

  setIncludeInFinancialRecords(merchantId: string, includeInFinancialRecords: boolean): Observable<boolean> {
    return this.merchantApi
      .setIncludeInFinancialRecords({
        merchantId: merchantId,
        includeInFinancialRecords: includeInFinancialRecords,
      })
      .pipe(map((response) => response.ok));
  }

  sendSalesInvoiceEmail(merchantId: string, invoiceId: string): Observable<boolean> {
    return this.merchantApi
      .sendSalesInvoiceEmail({
        merchantId: merchantId,
        invoiceId: invoiceId,
      })
      .pipe(map(() => true));
  }

  chargeSalesInvoice(merchantId: string, invoiceId: string, amount: number): Observable<boolean> {
    return this.merchantApi
      .chargeSalesInvoice({
        merchantId,
        invoiceId,
        amount,
      })
      .pipe(map(() => true));
  }

  sendSalesInvoiceReceiptEmail(merchantId: string, invoiceId: string): Observable<boolean> {
    return this.merchantApi
      .sendSalesInvoiceReceiptEmail({
        merchantId: merchantId,
        invoiceId: invoiceId,
      })
      .pipe(map(() => true));
  }

  private apiOptions(): { responseType: 'blob'; withCredentials: boolean } {
    return {
      responseType: 'blob',
      withCredentials: true,
    };
  }

  createExternalAccount(merchantId: string, stripeToken: string): Observable<boolean> {
    return this.merchantApi
      .createStripeExternalAccount(
        new CreateStripeExternalAccountRequest({
          merchantId,
          stripeToken,
        }),
      )
      .pipe(map(() => true));
  }

  generateAccountBalanceReport(merchantId: string): Observable<Blob> {
    const queryParams = `?merchantID=${merchantId}`;
    return this.http
      .get(this.hostService.hostWithScheme + '/merchant-balance-details-report' + queryParams, this.apiOptions())
      .pipe(share(), catchError(handleCsvError));
  }

  getMultiRetailConfigurations(
    merchantId: string,
    retailGroupIds: string[],
  ): Observable<Map<string, RetailConfiguration>> {
    const request = new GetMultiRetailConfigurationsRequest({
      merchantId,
      retailGroupIds,
    });
    return this.merchantApi.getMultiRetailConfigurations(request).pipe(
      map((resp: GetMultiRetailConfigurationsResponse) => {
        const configMap = new Map<string, RetailConfiguration>();
        retailGroupIds.forEach((groupId) => {
          configMap.set(groupId, retailConfigurationFromApi(resp.retailConfigurations[groupId]));
        });
        return configMap;
      }),
    );
  }

  upsertRetailConfiguration(
    merchantId: string,
    retailGroupId: string,
    currencyCode: string,
    currencyConversionRate?: number,
  ): Observable<null> {
    const request = new UpsertRetailConfigurationRequest({
      merchantId,
      retailGroupId,
      currencyCode,
      currencyConversionRate,
    });
    return this.merchantApi.upsertRetailConfiguration(request).pipe(map(() => null));
  }

  getPayoutSummary(merchantId: string): Observable<PayoutSummary> {
    return this.merchantApi
      .getPayoutSummary(
        new GetPayoutSummaryRequest({
          merchantId,
        }),
      )
      .pipe(map((resp) => payoutSummaryFromAPI(resp)));
  }

  getRetailPayout(merchantId: string, payoutId: string): Observable<RetailPayout> {
    return this.merchantApi
      .getRetailPayout(
        new GetRetailPayoutRequest({
          merchantId,
          payoutId,
        }),
      )
      .pipe(map((resp) => retailPayoutFromApi(resp.payout)));
  }

  listRetailPayouts(merchantId: string, cursor = '', pageSize = 10): Observable<PagedResponse<RetailPayout>> {
    return this.merchantApi
      .listRetailPayouts(
        new ListRetailPayoutsRequest({
          merchantId: merchantId,
          pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
        }),
      )
      .pipe(
        map((resp: ListRetailPayoutsResponse) => {
          const retailPayouts = (resp.payouts || []).map((payout) => retailPayoutFromApi(payout));
          return new PagedResponse(retailPayouts, resp.pagingMetadata.nextCursor, resp.pagingMetadata.hasMore);
        }),
      );
  }

  /**
   * Updates the default retail customer configuration for a given merchant and group ID
   * @param merchantId - the merchant to update for
   * @param retailGroupId - the group ID to update for
   * @param defaultCustomerConfiguration - the default customer configuration to apply. If `null`, the merchant/group will have no defaults
   */
  upsertDefaultRetailCustomerConfiguration(
    merchantId: string,
    retailGroupId: string,
    defaultCustomerConfiguration?: DefaultCustomerConfiguration,
  ): Observable<HttpResponse<null>> {
    return this.merchantApi.upsertDefaultRetailCustomerConfiguration(
      new UpsertDefaultRetailCustomerConfigurationRequest({
        merchantId,
        retailGroupId,
        defaultCustomerConfiguration: defaultCustomerConfigurationToApi(defaultCustomerConfiguration),
      }),
    );
  }

  /**
   * Creates a new report run for a merchant.
   *
   * @param merchantId is the id of the merchant to create the report for.
   * @param reportType is the type of report to generate
   * @param intervalStart is the starting time for entities in the report.
   * @param intervalEnd is the end range for entities in the report.
   * @param currencyCode fill filter results down to a specific currency. This is optional.
   */
  createReportRun(
    merchantId: string,
    reportType: MerchantServicesReportRunType,
    intervalStart: Date,
    intervalEnd: Date,
    currencyCode?: string,
  ): Observable<MerchantServicesReportRun> {
    return this.merchantApi.createReportRun({
      merchantId,
      reportType,
      intervalStart,
      intervalEnd,
      currencyCode,
    });
  }

  /**
   * Get a report run by its identifier.
   *
   * @param id is the id of the report run.
   */
  getReportRun(id: string): Observable<MerchantServicesReportRun> {
    return this.merchantApi.getReportRun({ id });
  }

  /**
   * Gets the data from a report run
   *
   * @param id is the id of the report run.
   */
  getReport(id: string): Observable<Blob> {
    return this.http
      .get(this.hostService.hostWithScheme + `/merchant-services/reports/${id}`, this.apiOptions())
      .pipe(catchError(handleCsvError));
  }

  getRetailAccountBalance(merchantId: string): Observable<AccountBalance> {
    return this.merchantApi.getRetailAccountBalance({ merchantId }).pipe(map((resp) => resp.balance));
  }
}
